/**
 * A small Markdown renderer for post bodies.
 *
 * Deliberately hand-rolled rather than a library, for one reason that matters:
 * every general-purpose Markdown renderer passes raw HTML straight through,
 * which means shipping one turns post content into an XSS vector the day
 * anything but us can write a post. This one never produces HTML it did not
 * construct itself — there is no path from post text to markup — so the worst
 * a hostile post can do is look wrong.
 *
 * It covers what the Cyber Brief actually uses: headings, bold, italic, links,
 * inline code, bullet and numbered lists, blockquotes, and rules.
 */

/**
 * Inline formatting, returned as React nodes rather than an HTML string.
 *
 * Note the italic rules capture the preceding character rather than using a
 * lookbehind. `(?<!...)` is a *parse-time* syntax error in Safari before 16.4,
 * which would take down the entire bundle rather than just this file — the
 * kind of failure that only shows up on someone else's machine.
 */
function inline(text, keyPrefix = '') {
  const out = [];
  let rest = String(text);
  let key = 0;

  // Each rule is [pattern, render, leadGroup]. When leadGroup is set, that
  // capture group is the character before the marker and is re-emitted as
  // plain text, which is how a word boundary is enforced without a lookbehind.
  const RULES = [
    [/`([^`]+)`/, (m) => <code key={`${keyPrefix}c${key++}`}>{m[1]}</code>],
    [/\[([^\]]+)\]\(([^)\s]+)\)/, (m) => {
      // Only http(s), mailto and site-relative links become anchors. This is
      // what stops `[click](javascript:...)` from ever reaching the DOM.
      const href = m[2];
      const safe = /^(https?:\/\/|mailto:|\/)/i.test(href);
      if (!safe) return <span key={`${keyPrefix}l${key++}`}>{m[1]}</span>;
      const external = /^https?:\/\//i.test(href);
      return (
        <a key={`${keyPrefix}l${key++}`} href={href}
           {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
          {m[1]}
        </a>
      );
    }],
    [/\*\*([^*]+)\*\*/, (m) => <strong key={`${keyPrefix}b${key++}`}>{m[1]}</strong>],
    [/(^|[^\w*])\*([^*\n]+)\*(?![\w*])/, (m) => <em key={`${keyPrefix}i${key++}`}>{m[2]}</em>, 1],
    [/(^|[^\w_])_([^_\n]+)_(?![\w_])/, (m) => <em key={`${keyPrefix}u${key++}`}>{m[2]}</em>, 1],
  ];

  while (rest) {
    let best = null;
    for (const [pattern, render, leadGroup] of RULES) {
      const m = rest.match(pattern);
      if (!m) continue;
      // Where the marker actually starts, ignoring any captured lead character.
      const start = m.index + (leadGroup ? (m[leadGroup] || '').length : 0);
      if (best === null || start < best.start) best = { m, render, leadGroup, start };
    }
    if (!best) break;

    // Everything before the match, plus the lead character the pattern ate.
    const before = rest.slice(0, best.start);
    if (before) out.push(before);
    out.push(best.render(best.m));
    rest = rest.slice(best.m.index + best.m[0].length);
  }

  if (rest) out.push(rest);
  return out;
}

export default function Markdown({ text }) {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { i++; continue; }

    // Horizontal rule
    if (/^\s*([-*_])\s*(\1\s*){2,}$/.test(line)) {
      blocks.push(<hr className="rule" key={i} style={{ margin: '2rem 0' }} />);
      i++;
      continue;
    }

    // Heading
    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      const level = Math.min(heading[1].length + 1, 4);   // # in a post is an h2
      const Tag = `h${level}`;
      blocks.push(<Tag key={i}>{inline(heading[2], `h${i}`)}</Tag>);
      i++;
      continue;
    }

    // Blockquote
    if (/^\s*>/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) {
        buf.push(lines[i].replace(/^\s*>\s?/, ''));
        i++;
      }
      blocks.push(<blockquote key={`q${i}`} className="railed">{inline(buf.join(' '), `q${i}`)}</blockquote>);
      continue;
    }

    // Lists
    const bullet = /^\s*[-*+]\s+/;
    const number = /^\s*\d+[.)]\s+/;
    if (bullet.test(line) || number.test(line)) {
      const ordered = number.test(line);
      const pattern = ordered ? number : bullet;
      const items = [];
      while (i < lines.length && pattern.test(lines[i])) {
        items.push(lines[i].replace(pattern, ''));
        i++;
      }
      const Tag = ordered ? 'ol' : 'ul';
      blocks.push(
        <Tag key={`l${i}`}>
          {items.map((it, n) => <li key={n}>{inline(it, `l${i}-${n}`)}</li>)}
        </Tag>
      );
      continue;
    }

    // Paragraph — consecutive non-blank lines belong together.
    const buf = [];
    while (
      i < lines.length && lines[i].trim() &&
      !/^(#{1,4}\s|\s*>|\s*[-*+]\s|\s*\d+[.)]\s)/.test(lines[i])
    ) {
      buf.push(lines[i]);
      i++;
    }
    blocks.push(<p key={`p${i}`}>{inline(buf.join(' '), `p${i}`)}</p>);
  }

  return <div className="prose">{blocks}</div>;
}
